// Vorschlag für das Feld „Kontoinhaber" einer Bankverbindung: der Name des
// Kontakts, dem die Verbindung gehört. Bewusst eine eigene Datei, weil die
// Regel an mehreren Stellen gebraucht wird (Verwalten-Dialog der Akte, Anlage)
// und weil sie NICHT dasselbe liefert wie `displayContactName`:
//
//   displayContactName → "Mustermann, Max"   (Listenform, Nachname zuerst)
//   kontoinhaberVorschlag → "Max Mustermann" (so steht es auf dem Konto)
//
// Für Firmen gibt es kein `companyName` im Datenmodell — der Firmenname steht
// allein in `lastname`, ein abgeschnittener Name wird in `suffix` fortgesetzt
// (Contact.buildContactDisplayName). Der Vorname einer Firma ist im Altbestand
// oft Müll (ein einzelner Punkt) und darf deshalb nie einfließen.
//
// Seit 02.10.2026 wird der Vorschlag überall, wo eine Bankverbindung ANGELEGT
// wird, auch gespeichert — nicht mehr nur angezeigt. Ein Rechtsform-Kürzel
// wird bewusst NICHT angehängt (User-Entscheidung 02.10.2026: „machen die
// Mitarbeiter"). Dieselbe Regel nutzt das Bereinigungsskript
// apps/api/scripts/bereinigung/2026-10-02-kontoinhaber-erzeugen.ts.

export interface KontoinhaberQuelle {
  firstName?: string | null;
  vorname?: string | null;
  lastname?: string | null;
  suffix?: string | null;
}

/**
 * Name des Kontakts in Kontoinhaber-Schreibweise, oder "" wenn nichts da ist.
 * `istFirma` entscheidet, ob der Vorname mitgenommen wird; bei Firmen zählen
 * nur Name und Namenszusatz.
 */
export function kontoinhaberVorschlag(
  kontakt: KontoinhaberQuelle | null | undefined,
  istFirma: boolean,
): string {
  if (!kontakt) return "";
  const teil = (v: unknown) => String(v ?? "").trim();
  const nachname = teil(kontakt.lastname);
  if (istFirma) {
    // Namenszusatz mit Leerzeichen anhängen — nie mit Komma, er setzt den
    // Firmennamen fort, er ergänzt ihn nicht.
    return [nachname, teil(kontakt.suffix)].filter(Boolean).join(" ").replace(/\s+/g, " ");
  }
  const vorname = teil(kontakt.firstName) || teil(kontakt.vorname);
  return [vorname, nachname].filter(Boolean).join(" ");
}

/**
 * Ist eine Bankverbindung mehr als der vorbelegte Kontoinhaber? Nur dann wird
 * sie gespeichert — sonst entstünde aus jedem angefangenen Eintrag eine leere
 * Bankzeile mit Namen. Ein abweichend getippter Inhaber zählt als Angabe.
 */
export function bankHatAngaben(
  felder: unknown[],
  kontoinhaber: string | null | undefined,
  vorgabe: string,
): boolean {
  if (felder.some((v) => String(v ?? "").trim())) return true;
  const inhaber = String(kontoinhaber ?? "").trim();
  return !!inhaber && inhaber !== vorgabe.trim();
}
